import { HttpApiEndpoint, HttpApiGroup } from "@effect/platform";
import * as S from "effect/Schema";
import { PrincipalMiddleware } from "./principal.js";
import { Forbidden } from "./shares.js";
import { PagingParams, pagedResponse } from "./paging.js";
import { LanguageSchema } from "./column-schemas.js";
/**
 * The dashboard online-courses card's three server-side counts, folding the
 * legacy screen's three sequential `online_course_shares`/
 * `vw_online_course_shares`/`vw_employee_online_course_assignments`
 * aggregate fetches into one round trip. `clientAccessCount` has no
 * `client_id` scope — it counts the course's own client-level shares
 * (`online_course_shares.user_id IS NULL`), matching the legacy admin
 * "clients" metric. `sharedCount`/`assignedCount` are always scoped to
 * `client_id`, and location-scoped only for a location-manager, from that
 * caller's own `location_ids` grant — every other role reads the client's
 * full totals, so the `location_ids` param never narrows them. An empty
 * location-manager grant counts nothing.
 */
export const OnlineCourseMetrics = S.Struct({
    clientAccessCount: S.Int,
    sharedCount: S.Int,
    assignedCount: S.Int,
});
const UuidCsvList = S.transform(S.String, S.Array(S.UUID), {
    strict: true,
    decode: (s) => s
        .split(",")
        .map((t) => t.trim())
        .filter((t) => t.length > 0),
    encode: (ids) => ids.join(","),
});
export const GetOnlineCourseMetricsParams = S.Struct({
    online_course_id: S.UUID,
    translation_group_id: S.NumberFromString.pipe(S.int()),
    client_id: S.UUID,
    language: LanguageSchema,
    location_ids: S.optional(UuidCsvList),
});
export const GetOnlineCourseMetrics = HttpApiEndpoint.get("getOnlineCourseMetrics", "/dashboard/online-courses/metrics")
    .setUrlParams(GetOnlineCourseMetricsParams)
    .addSuccess(S.Struct({ data: OnlineCourseMetrics }))
    .addError(Forbidden, { status: 403 });
/**
 * A `vw_online_course_shares` row scoped to one caller (or the client-level
 * share when no user is targeted) — the online-course detail screen's asset.
 * Mirrors the `vw_online_course_shares` columns the screen actually reads;
 * distinct from `online-courses.OnlineCourseRow` (the admin catalog's own
 * editor read model over the base `online_courses` table).
 */
export const OnlineCourseShareDetail = S.Struct({
    id: S.UUID,
    online_course_id: S.UUID,
    translation_group_id: S.NullOr(S.Int),
    client_id: S.UUID,
    user_id: S.NullOr(S.UUID),
    title: S.NullOr(S.String),
    description: S.NullOr(S.String),
    url: S.NullOr(S.String),
    thumbnail: S.NullOr(S.UUID),
    thumbnail_paperform: S.NullOr(S.String),
    is_mandatory: S.NullOr(S.Boolean),
    is_initial_audit: S.NullOr(S.Boolean),
    language: S.NullOr(LanguageSchema),
    location_ids: S.NullOr(S.String),
    available_languages: S.NullOr(S.Array(S.String)),
    completed: S.NullOr(S.Boolean),
    completed_at: S.NullOr(S.String),
    metadata: S.NullOr(S.Unknown),
    share_date: S.NullOr(S.String),
    shared_by: S.NullOr(S.UUID),
    shared_by_first_name: S.NullOr(S.String),
    shared_by_last_name: S.NullOr(S.String),
});
const MyOnlineCourseBase = S.extend(OnlineCourseShareDetail, S.Struct({
    paperform_custom_slug: S.NullOr(S.String),
    paperform_slug: S.NullOr(S.String),
    user_email: S.NullOr(S.String),
    user_first_name: S.NullOr(S.String),
    user_last_name: S.NullOr(S.String),
}));
export const MyOnlineCourseRow = S.Union(S.extend(MyOnlineCourseBase, S.Struct({
    kind: S.Literal("client"),
    measure_assignment_due_date: S.Null,
    measure_assignment_id: S.Null,
    measure_assignment_status: S.Null,
})), S.extend(MyOnlineCourseBase, S.Struct({
    kind: S.Literal("shared"),
    measure_assignment_due_date: S.Null,
    measure_assignment_id: S.Null,
    measure_assignment_status: S.Null,
})), S.extend(MyOnlineCourseBase, S.Struct({
    kind: S.Literal("assigned"),
    measure_assignment_due_date: S.NullOr(S.String),
    measure_assignment_id: S.UUID,
    measure_assignment_status: S.NullOr(S.String),
})));
export const ListMyOnlineCoursesParams = S.extend(PagingParams, S.Struct({
    kind: S.Literal("shared", "assigned", "client"),
    client_id: S.optional(S.UUID),
    language: LanguageSchema,
    search: S.optional(S.String),
}));
/** Shared or assigned courses for the authenticated employee. */
export const ListMyOnlineCourses = HttpApiEndpoint.get("listMyOnlineCourses", "/employee/online-courses")
    .setUrlParams(ListMyOnlineCoursesParams)
    .addSuccess(pagedResponse(MyOnlineCourseRow))
    .addError(Forbidden, { status: 403 });
export const OnlineCourseShareNotFound = S.Struct({
    _tag: S.Literal("OnlineCourseShareNotFound"),
});
export const GetOnlineCourseShareParams = S.Struct({
    online_course_id: S.UUID,
    client_id: S.UUID,
    user_id: S.optional(S.UUID),
});
/**
 * `user_id` omitted resolves the client-level share (`user_id IS NULL`) —
 * the admin/ohs-officer/location-manager detail view; `user_id` present
 * resolves the caller's own per-user share — the employee detail view. The
 * caller may only ever pass their own id (never another user's).
 */
export const GetOnlineCourseShare = HttpApiEndpoint.get("getOnlineCourseShare", "/dashboard/online-courses/share")
    .setUrlParams(GetOnlineCourseShareParams)
    .addSuccess(S.Struct({ data: OnlineCourseShareDetail }))
    .addError(OnlineCourseShareNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
export const onlineCourseSharesGroup = HttpApiGroup.make("online-course-shares")
    .add(GetOnlineCourseMetrics)
    .add(GetOnlineCourseShare)
    .add(ListMyOnlineCourses)
    .middleware(PrincipalMiddleware);
//# sourceMappingURL=online-course-shares.js.map